import { Directive, ElementRef, HostBinding, Input } from '@angular/core';

/**
 * enums
*/
import { IButtonSize } from './enums/button-size';
import { IButtonSocial } from './enums/button-social';
import { IButtonAppearance } from './enums/button-appearance';
import { IThemeColorVariant } from '@core/enums/theme-colors-variants';

/**
 * interfaces
 */
import { IButtonOptions } from './interfaces/button-options';

@Directive({
  selector: '[appButton]',
  host: {
    '[class.btn]': 'true',
    '[class]': '_buildButtonClass',
  }
})
export class ButtonDirective implements IButtonOptions {

  /**
   * @description bootstrap Variants ( Primary, etc.. )
   */
  @Input({ alias: "variant" })
  public variant!: IThemeColorVariant


  /**
   * @description bootstrap Appearance ( Label, Outline, Text )
   */
  @Input({ alias: "appearance" })
  public appearance?: IButtonAppearance | null


  /**
   * @description Vuexy Button ( Facebook, etc.. )
   */
  @Input({ alias: "social" })
  public social!: IButtonSocial


  /**
   * @description bootstrap button sizes ( xs, sm, md, lg, xl )
   */
  @Input({ alias: "size" })
  public size!: IButtonSize

  /**
   * @description html attr Id
   */
  @HostBinding('attr.id')
  @Input({ alias: "id"}) public ID!: string;

  /**
   * @description button type ( button, submit, reset )
   */
  @HostBinding('attr.type')
  @Input({ alias: "type"}) public type: 'button' | 'submit' | 'reset' | null = "button";

  /**
   * @description disabled status
   */
  @HostBinding('attr.disabled')
  public get disabledAttribute(): string | null {
    return this.disabled ? 'disabled' : null;
  }

  @Input({ alias: "disabled"})
  public disabled: boolean = false;

  /**
   * @description if true add active class in button
   */
  @HostBinding('class.active')
  @HostBinding('attr.aria-pressed')
  @Input({ alias: "active"}) public active: boolean = false;

  /**
   * @description if true add rounded-pill class in button
   */
  @HostBinding('class.rounded-pill')
  @Input({ alias: "rounded"}) public rounded: boolean = false;

  /**
   * @description if true add btn-icon class in button
   */
  @HostBinding('class.btn-icon.d-inline-flex')
  @Input({ alias: "iconOnly"}) public iconOnly: boolean = false;

  /**
   * @description handler button ui depend on variant with appearance/Social
   */
  private get _buttonClass(): string {

    // button prefix
    const _prefix: string = 'btn';

    // append _prefix as first index
    const classMapping: string[] = [_prefix];

    // build button type
    if (typeof this.appearance == 'string') classMapping.push(this.appearance);
    if (typeof this.social == 'string') classMapping.push(this.social);
    if (typeof this.variant == 'string' && !this.social) classMapping.push(this.variant);

    return classMapping.join('-');

  }

  /**
   * @description mapped btn size
   * @example
   * class="btn-{sm}"
   */
  private get _buttonSize(): string | null {
    return this.size ? `btn-${this.size}` : null;
  }

  /**
   * @description get all class values to combine them with on string
   * @example "btn-primary btn-sm"
   */
  protected get _buildButtonClass(): string {

    // init
    let classContainer: (string | null)[] = [];

    // push core button class
    classContainer.push(this._buttonClass);

    // push button sizes
    classContainer.push(this._buttonSize);

    // filter nullable values
    classContainer = classContainer.filter(Boolean)

    return classContainer.join(' ');

  }

  /**
   *
   */
  constructor(public ElRef: ElementRef<HTMLButtonElement>) {}

}
